<template>
  <VeeForm v-slot="{ meta: formMeta }" @submit="submit">
    <div class="mb-16">
      <label for="firstName" class="form-label">
        {{ $label('form.firstName') }}
      </label>
      <VeeField
        v-slot="{ meta, field, errorMessage }"
        v-model="firstName"
        name="firstName"
        rules="required"
      >
        <input
          v-bind="field"
          id="first-name"
          type="text"
          :class="[
            'form-control',
            { 'is-valid': meta.valid && meta.touched },
            { 'is-invalid': !meta.valid && meta.touched }
          ]"
          aria-describedby="first-name"
        />
        <div v-show="!meta.valid && meta.touched" class="invalid-feedback">
          {{ errorMessage }}
        </div>
      </VeeField>
    </div>

    <div class="mb-16">
      <label for="lastName" class="form-label">
        {{ $label('form.lastName') }}
      </label>
      <VeeField
        v-slot="{ meta, field, errorMessage }"
        v-model="lastName"
        name="lastName"
        rules="required"
      >
        <input
          v-bind="field"
          id="last-name"
          type="text"
          :class="[
            'form-control',
            { 'is-valid': meta.valid && meta.touched },
            { 'is-invalid': !meta.valid && meta.touched }
          ]"
          aria-describedby="last-name"
        />
        <div v-show="!meta.valid && meta.touched" class="invalid-feedback">
          {{ errorMessage }}
        </div>
      </VeeField>
    </div>

    <div class="mb-16">
      <label for="email" class="form-label">
        {{ $label('form.email') }}
      </label>
      <VeeField
        v-slot="{ meta, field, errorMessage }"
        v-model="email"
        name="email"
        rules="required|email"
        @blur="checkEmail"
      >
        <input
          v-bind="field"
          id="email"
          type="email"
          :class="[
            'form-control',
            { 'is-valid': meta.valid && meta.touched },
            { 'is-invalid': !meta.valid && meta.touched }
          ]"
          autocomplete="email"
          aria-describedby="email"
        />
        <div v-show="!meta.valid && meta.touched" class="invalid-feedback">
          {{ errorMessage }}
        </div>
      </VeeField>

      <SuggestedEmail
        :suggested-email="suggestedEmail"
        :update-email="updateEmail"
      />
    </div>

    <div class="mb-32">
      <label for="newPassword" class="form-label">
        {{ $label('form.password') }}
      </label>

      <VeeField
        v-slot="{ meta, field, errorMessage }"
        v-model="password"
        name="newPassword"
        rules="required|min:6"
      >
        <input
          v-bind="field"
          id="newPassword"
          :type="passwordFieldType"
          :is-valid="meta.valid && meta.touched"
          :class="[
            'form-control',
            { 'is-valid': meta.valid && meta.touched },
            { 'is-invalid': !meta.valid && meta.touched }
          ]"
          aria-describedby="password-explain-text"
          autocomplete="new-password"
        />
        <div v-show="!meta.valid && meta.touched" class="invalid-feedback">
          {{ errorMessage }}
        </div>
      </VeeField>

      <PasswordToggle :on-toggle="togglePasswordField" />
    </div>

    <BaseButton
      variant="primary"
      :disabled="isLoading || !formMeta.valid || !formMeta.touched"
      :class="{ loading: isLoading }"
      data-nw="registration-submit"
      type="submit"
    >
      {{ $label(isB2b ? 'user.signUp.b2b.submitBtn' : submitBtnLabel) }}
    </BaseButton>

    <p v-if="isB2b" class="caption text-muted mt-8 mt-md-16">
      {{ $label('user.signUp.termsAndConditions') }}
      <NuxtLink class="text-muted" to="/algemene-voorwaarden">
        {{ $label('common.footer.toc') }}
      </NuxtLink>
    </p>

    <NuxtLink
      v-if="loginPath"
      class="mt-16 d-block"
      :to="{ path: loginPath, query: $route.query }"
      data-nw="register-to-login-link"
    >
      {{ $label('user.signIn.backToLogin') }}
    </NuxtLink>
  </VeeForm>
</template>

<script>
import ValidationMixin from '~/mixins/validation'
import PasswordToggle from '~/components/PasswordToggle'
import SuggestedEmail from '~/components/forms/SuggestedEmail'

export default {
  components: {
    PasswordToggle,
    SuggestedEmail
  },
  mixins: [ValidationMixin],
  props: {
    isB2b: {
      type: Boolean,
      default: false
    },
    onSubmit: {
      type: Function,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    loginPath: {
      type: String,
      default: ''
    },
    submitBtnLabel: {
      type: String,
      default: 'common.nextStep'
    }
  },
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      passwordFieldType: 'password',
      iconName: 'eye-show',
      togglePasswordLabel: 'form.showPassword',
      suggestedEmail: ''
    }
  },
  computed: {
    supportEmail() {
      const labelKey = 'common.supportEmail'
      const email = this.$label(labelKey)

      return email === labelKey ? '' : `mailto:${email}`
    }
  },
  methods: {
    submit() {
      try {
        this.onSubmit({
          firstName: this.firstName.trim(),
          lastName: this.lastName.trim(),
          email: this.email.trim(),
          password: this.password.trim()
        })
        // eslint-disable-next-line no-empty
      } catch {}
    },
    togglePasswordField(type) {
      this.passwordFieldType = type
    },
    isDisabled(formMeta) {
      if (this.isLoading) {
        return true
      }

      return !formMeta.touched || !formMeta.valid
    }
  }
}
</script>
